import { useState } from 'react';

import includes from 'lodash/includes';
import invoke from 'lodash/invoke';
import isEqual from 'lodash/isEqual';

import { IDocument } from '../../interfaces';
import {
  getFromSessionStorage,
  setSessionStorage,
} from '../../services/sessionStorage';

export type TSignedDocument = {
  signature?: string;
  initials?: string;
} & IDocument;

export type TUseSignedDocuments = {
  documents: TSignedDocument[];
  SESSION_STORAGE_KEY: string;
};

export function useSignedDocuments({
  documents,
  SESSION_STORAGE_KEY,
}: TUseSignedDocuments) {
  const [signedDocuments, setSignedDocuments] = useState(() => {
    const cachedDocuments: TSignedDocument[] = JSON.parse(
      getFromSessionStorage(SESSION_STORAGE_KEY) || '[]'
    );
    cachedDocuments.filter((cachedDoc) =>
      includes(
        documents.map((quoteDoc) => quoteDoc.url),
        cachedDoc.url
      )
    );
    return cachedDocuments || [];
  });

  const signedDocumentResponse = {
    signature: signedDocuments?.[0]?.signature,
    initials: signedDocuments?.[0]?.initials,
  };

  function handleSetSignedDocuments(
    document: IDocument,
    inputValues: { signature: string; initials: string }
  ) {
    const existingDoc = signedDocuments.find((doc) => doc.url === document.url);

    if (existingDoc) {
      const newSignedDocuments = signedDocuments.map((doc) => {
        if (doc.url === existingDoc.url) {
          return {
            ...document,
            signature: invoke(inputValues?.signature || '', 'trim'),
            initials: invoke(inputValues?.initials || '', 'trim'),
          };
        }
        return doc;
      });

      setSessionStorage(
        SESSION_STORAGE_KEY,
        JSON.stringify(newSignedDocuments)
      );
      setSignedDocuments(newSignedDocuments);

      return newSignedDocuments;
    }

    const newSignedDocuments = [
      ...signedDocuments,
      {
        ...document,
        signature: invoke(inputValues?.signature || '', 'trim'),
        initials: invoke(inputValues?.initials || '', 'trim'),
      },
    ];
    setSessionStorage(SESSION_STORAGE_KEY, JSON.stringify(newSignedDocuments));
    setSignedDocuments(newSignedDocuments);

    return newSignedDocuments;
  }

  function areAllDocumentsSigned() {
    const docUrls = documents.map((doc) => doc.url);
    const signedDocChecklist = signedDocuments.map((doc) => doc.url);
    return isEqual([...docUrls].sort(), [...signedDocChecklist].sort());
  }

  function isDocumentSigned(document: IDocument | TSignedDocument) {
    return includes(
      signedDocuments.map((doc) => doc.url),
      document.url
    );
  }

  function isLastDocument() {
    const actualSignedDocuments = signedDocuments.filter((doc) => {
      const document = documents.find((_doc) => _doc.name === doc.name);
      if (document && doc.url !== document.url) {
        return false;
      }
      return true;
    });

    return actualSignedDocuments.length + 1 === documents.length;
  }

  return {
    signedDocuments,
    signedDocumentResponse,
    handleSetSignedDocuments,
    areAllDocumentsSigned,
    isDocumentSigned,
    isLastDocument,
  };
}
